import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition, ShapeData } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect, getShapeData } from '../../entityTypes';
import { CanvasShape } from './ShapeNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { RectangleHorizontal, Circle, Diamond, Triangle, ToggleLeft, ToggleRight, Layers, Trash2 } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const SHAPE_TYPE_OPTIONS = [
  { id: 'rect', label: t('canvasEntity.shapeRect') },
  { id: 'ellipse', label: t('canvasEntity.shapeEllipse') },
  { id: 'diamond', label: t('canvasEntity.shapeDiamond') },
  { id: 'triangle', label: t('canvasEntity.shapeTriangle') },
];

const CORNER_RADIUS_OPTIONS = [
  { id: '0', label: '0px' },
  { id: '4', label: '4px' },
  { id: '8', label: '8px' },
  { id: '12', label: '12px' },
  { id: '9999', label: t('canvasEntity.round') },
];

const SHAPE_BG_COLOR_OPTIONS = [
  { id: 'lightblue', label: t('canvasEntity.colorLightBlue'), color: '#dbeafe' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#f3f4f6' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#dcfce7' },
  { id: 'yellow', label: t('canvasEntity.colorYellow'), color: '#fef9c3' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#fee2e2' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: '#ede9fe' },
  { id: 'white', label: t('canvasEntity.colorWhite'), color: '#ffffff' },
];

export class ShapeHandler extends EntityKindHandler {
  readonly kind = 'shape';
  readonly Component = CanvasShape;
  readonly dataAttribute = 'data-canvas-shape';
  canConnect = true;
  canResize = true;

  getAddPickerEntry(): AddPickerEntry {
    return {
      label: 'Shape',
      icon: RectangleHorizontal,
      iconColor: '#6366f1',
      priority: 25,
      mode: 'instant',
      children: [
        { id: 'rect', label: t('canvasEntity.shapeRect'), icon: RectangleHorizontal },
        { id: 'ellipse', label: t('canvasEntity.shapeEllipse'), icon: Circle },
        { id: 'diamond', label: t('canvasEntity.shapeDiamond'), icon: Diamond },
        { id: 'triangle', label: t('canvasEntity.shapeTriangle'), icon: Triangle },
      ],
    };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (!entity.connectable) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const d = getShapeData(entity);
    const actions: ToolbarAction[] = [
      {
        name: 'shapeType',
        label: t('canvasEntity.shape'),
        type: 'select',
        options: SHAPE_TYPE_OPTIONS,
        getValue: () => d.shapeType,
        apply: (v) => store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { shapeType: v as ShapeData['shapeType'] } }),
      },
      {
        name: 'backgroundColor',
        label: t('canvasEntity.background'),
        type: 'color',
        options: SHAPE_BG_COLOR_OPTIONS,
        getValue: () => d.backgroundColor,
        apply: (v) => store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { backgroundColor: v as string } }),
      },
      {
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => d.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      },
      {
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => d.borderColor ? String(d.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      },
    ];

    // Corner radius only for rect
    if (d.shapeType === 'rect') {
      actions.push({
        name: 'cornerRadius',
        label: t('canvasEntity.cornerRadius'),
        type: 'select',
        options: CORNER_RADIUS_OPTIONS,
        getValue: () => String(d.cornerRadius ?? 0),
        apply: (v) => store.send({ type: 'shape.updateData', entityId: entity.id, dataChanges: { cornerRadius: Number(v) } }),
      });
    }

    actions.push({
      name: 'connectable',
      label: 'Connectors',
      type: 'toggle',
      getValue: () => entity.connectable,
      apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
    });

    return actions;
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const shapeId = entity.id;
    const d = getShapeData(entity);
    const items: ContextMenuItem[] = [];

    // --- Shape type submenu ---
    items.push({
      id: 'shape-type-submenu',
      label: t('canvasEntity.shape'),
      children: SHAPE_TYPE_OPTIONS.map(opt => ({
        id: `shape-type-${opt.id}`,
        label: `${d.shapeType === opt.id ? '\u25CF ' : ''}${opt.label}`,
        onClick: () => store.send({ type: 'shape.updateData', entityId: shapeId, dataChanges: { shapeType: opt.id as ShapeData['shapeType'] } }),
      })),
    });

    // --- Background color submenu ---
    items.push(buildColorGridSubmenu({
      id: 'bg-color-submenu',
      label: t('canvasEntity.backgroundColor'),
      separator: true,
      currentColor: d.backgroundColor,
      handlerColors: [...SHAPE_BG_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'shape.updateData', entityId: shapeId, dataChanges: { backgroundColor: color } }),
    }));

    // --- Border color submenu ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: d.borderColor,
      onSelectColor: (color) => store.send({ type: 'shape.updateData', entityId: shapeId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'shape.updateData', entityId: shapeId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Corner radius submenu (only for rect) ---
    if (d.shapeType === 'rect') {
      const currentRadius = d.cornerRadius ?? 0;
      items.push({
        id: 'corner-radius-submenu',
        label: t('canvasEntity.cornerRadius'),
        children: CORNER_RADIUS_OPTIONS.map(opt => ({
          id: `corner-${opt.id}`,
          label: `${currentRadius === Number(opt.id) ? '\u25CF ' : ''}${opt.label}`,
          onClick: () => store.send({ type: 'shape.updateData', entityId: shapeId, dataChanges: { cornerRadius: Number(opt.id) } }),
        })),
      });
    }

    // --- Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      separator: true,
      onClick: () => store.send({ type: 'entity.setConnectable', entityId: shapeId, connectable: !entity.connectable }),
    });

    // --- Gruppe submenu ---
    items.push({ ...buildGroupSubmenu(ctx), separator: true });

    // --- Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId: shapeId, layerId });
      }),
    });

    // --- Remove ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.remove'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: shapeId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.elements'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

}
